import { SAVED_ANSWERS, TASK_TYPE_INFO, type TaskType } from "@krut/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, unwrap } from "../api.ts";
import { useMe } from "../hooks.ts";
import { relative } from "../lib/cron.ts";
import { Card, cx, ErrorText } from "./ui.tsx";
import { BarList } from "./charts.tsx";
import { Icon } from "./icons.tsx";

/**
 * Time saved: each finished task the assistant records counts its type's standard time, or what the person
 * says it saved them. Asked under the reply in the chat, and on the dashboard for recent tasks.
 */

type Answer = (typeof SAVED_ANSWERS)[number]["minutes"];
export interface TaskView {
  id: number;
  type: string;
  at: string;
  standardMinutes: number;
  answerMinutes: number | null;
}

/** 40 → "40 min", 90 → "1.5 h", 120 → "2 h". */
export const fmtMinutes = (m: number) => (m < 60 ? `${Math.round(m)} min` : `${+(m / 60).toFixed(1)} h`);
/** Hours for totals: 0 → "0 h", 95 → "1.6 h", 1234 → "21 h". */
export const fmtHours = (m: number) => `${m >= 600 ? Math.round(m / 60).toLocaleString() : +(m / 60).toFixed(1)} h`;
export const typeLabel = (t: string | null) => (t ? (TASK_TYPE_INFO[t as TaskType]?.label ?? t) : "—");
/** Days and weeks are counted in the viewer's own time zone. */
export const browserZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

/** The person's own tasks among these ids (the ones a chat shows). */
export function useTasks(ids: number[]) {
  const key = [...new Set(ids)].sort((a, b) => a - b).join(",");
  return useQuery({
    queryKey: ["finished-tasks", key],
    queryFn: () => unwrap(api.me["time-saved"].tasks.$get({ query: { ids: key } })),
    enabled: key !== "",
    staleTime: 60_000,
  });
}

/** "(clock) Research · counts as 30 min · How much time did it save you? None · 15 min · 1 h · 2 h+" */
export function TaskCheck({ task, compact }: { task: TaskView; compact?: boolean }) {
  const qc = useQueryClient();
  const me = useMe();
  // An admin acting as someone sees their answers but can't give them.
  const readOnly = !!me.data?.user.impersonatedBy;
  const answer = useMutation({
    mutationFn: (minutes: Answer) => unwrap(api.me["time-saved"].tasks[":id"].answer.$put({ param: { id: String(task.id) }, json: { minutes } })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["finished-tasks"] });
      void qc.invalidateQueries({ queryKey: ["my-time-saved"] });
    },
  });
  const chosen = answer.isPending ? answer.variables : task.answerMinutes;
  const counts = chosen ?? task.standardMinutes;
  return (
    <div className={cx("flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-[var(--muted)]", !compact && "mt-3")}>
      {!compact && (
        <span className="inline-flex items-center gap-1.5">
          <Icon name="clock" size={13} className="shrink-0" />
          {typeLabel(task.type)} · {chosen === null ? `counts as ${fmtMinutes(counts)} saved` : chosen === 0 ? "you said it saved no time" : `you said it saved ${fmtMinutes(counts)}`}
        </span>
      )}
      {!readOnly && (
        <span className="flex flex-wrap items-center gap-2">
          {!compact && chosen === null && <span>How much time did it save you?</span>}
          <span role="group" aria-label="Time this saved you" className="inline-flex overflow-hidden rounded-full border border-[var(--line)]">
            {SAVED_ANSWERS.map((a) => (
              <button
                key={a.minutes}
                aria-pressed={chosen === a.minutes}
                disabled={answer.isPending}
                onClick={() => answer.mutate(a.minutes)}
                className={cx(
                  "border-l border-[var(--line)] px-2.5 py-1 transition first:border-l-0",
                  chosen === a.minutes ? "bg-[var(--chrome)] font-medium text-white" : "hover:bg-[var(--panel-2)] hover:text-[var(--text)]",
                )}
              >
                {a.label}
              </button>
            ))}
          </span>
        </span>
      )}
      {answer.error && <ErrorText error={answer.error} />}
    </div>
  );
}

/** The person's own numbers, on their dashboard. */
export function MyTimeSaved() {
  const q = useQuery({ queryKey: ["my-time-saved"], queryFn: () => unwrap(api.me["time-saved"].$get({ query: { tz: browserZone() } })) });
  if (!q.data) return q.error ? <ErrorText error={q.error} /> : null;
  const { week, month, all, since, byType, recent } = q.data;
  return (
    <Card className="mt-6 p-5">
      <h2 className="text-sm font-semibold">Time your assistant saved you</h2>
      {!since ? (
        <p className="mt-2 text-sm text-[var(--muted)]">
          Nothing counted yet. When your assistant finishes a piece of work for you (a draft, some research, a spreadsheet), it counts the time
          that would have taken here.
        </p>
      ) : (
        <>
          <div className="mt-4 grid grid-cols-3 gap-3">
            <Stat label="This week" minutes={week.minutes} tasks={week.tasks} />
            <Stat label="This month" minutes={month.minutes} tasks={month.tasks} />
            <Stat label={`Since ${new Date(since).toLocaleDateString(undefined, { month: "short", day: "numeric" })}`} minutes={all.minutes} tasks={all.tasks} />
          </div>
          <div className="mt-6 grid gap-6 lg:grid-cols-2">
            <div>
              <h3 className="mb-3 text-xs font-medium text-[var(--muted)]">This month, by type of work</h3>
              {!byType.length && <p className="text-sm text-[var(--muted)]">Nothing yet this month.</p>}
              <BarList rows={byType.map((t) => ({ key: t.type, label: typeLabel(t.type), value: t.minutes, note: `${fmtMinutes(t.minutes)} · ${t.tasks} task${t.tasks === 1 ? "" : "s"}` }))} />
            </div>
            <div>
              <h3 className="mb-2 text-xs font-medium text-[var(--muted)]">Recent tasks: how much time did each save you?</h3>
              <ul className="divide-y divide-[var(--line)]">
                {recent.map((t) => (
                  <li key={t.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
                    <span>
                      {typeLabel(t.type)} <span className="text-xs text-[var(--muted)]">· {relative(t.at)} · counts {fmtMinutes(t.answerMinutes ?? t.standardMinutes)}</span>
                    </span>
                    <TaskCheck task={t} compact />
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </>
      )}
      <p className="mt-5 text-xs text-[var(--muted)]">
        Your assistant records only the type of each finished task, never what it was. A task counts your workspace's standard time for that type
        until you say how much it saved you. Admins see these numbers for each person.
      </p>
    </Card>
  );
}

function Stat({ label, minutes, tasks }: { label: string; minutes: number; tasks: number }) {
  return (
    <div>
      <div className="text-xs text-[var(--muted)]">{label}</div>
      <div className="mt-2 text-[26px] leading-none font-semibold tracking-[-0.02em]">{fmtHours(minutes)}</div>
      <div className="mt-1.5 text-xs text-[var(--muted)]">{tasks} task{tasks === 1 ? "" : "s"}</div>
    </div>
  );
}
